import { name as packageName, version } from "../package.json";
import { initQueue } from "./queue";
import type { AllowedPropertyValues, AnalyticsProps, FlagsDataInput } from "./types";
import {
  isBrowser,
  parseProperties,
  setMode,
  isDevelopment,
  isProduction,
  computeRoute,
  getScriptSrc,
} from "./utils";

/**
 * Injects the Vuto Web Analytics script into the page head and starts tracking page views. Read more in our [documentation](https://vercel.com/docs/concepts/analytics/package).
 * @param [props] - Analytics options.
 * @param [props.mode] - The mode to use for the analytics script. Defaults to `auto`.
 *  - `auto` - Automatically detect the environment.  Uses `production` if the environment cannot be determined.
 *  - `production` - Always use the production script. (Sends events to the server)
 *  - `development` - Always use the development script. (Logs events to the console)
 */
function inject(
  props: AnalyticsProps & {
    framework?: string;
    basePath?: string;
  }
): void {
  if (!isBrowser()) return;

  setMode(props.mode);

  initQueue();

  const src = getScriptSrc(props);

  if (document.head.querySelector(`script[src*="${src}"]`)) return;

  const script = document.createElement("script");
  script.src = src;
  script.defer = true;
  script.dataset.sdkn = packageName + (props.framework ? `/${props.framework}` : "");
  script.dataset.sdkv = version;

  // Définir l'endpoint de collecte des données avec l'URL complète
  const scriptOrigin = new URL(src).origin;
  script.dataset.endpoint = `${scriptOrigin}/api/analytics`;
  script.dataset.projectId = props.projectId;

  script.onerror = (): void => {
    const errorMessage = isDevelopment()
      ? "Please check if any ad blockers are enabled and try again."
      : "Please check if the project is enabled in the Vuto Web Analytics dashboard.";

    // eslint-disable-next-line no-console -- Logging to console is intentional
    console.log(`[Vuto Web Analytics] Failed to load script from ${src}. ${errorMessage}`);
  };

  document.head.appendChild(script);
}

/**
 * Tracks a custom event. Please refer to the [documentation](https://vercel.com/docs/concepts/analytics/custom-events) for more information on custom events.
 * @param name - The name of the event.
 * * Examples: `Purchase`, `Click Button`, or `Play Video`.
 * @param [properties] - Additional properties of the event. Nested objects are not supported. Allowed values are `string`, `number`, `boolean`, and `null`.
 */
function track(
  name: string,
  properties?: Record<string, AllowedPropertyValues>,
  options?: {
    flags?: FlagsDataInput;
  }
): void {
  if (!isBrowser()) {
    const msg =
      "[Vuto Web Analytics] Please import `track` from `@vutolabs/analytics/server` when using this function in a server environment";

    if (isProduction()) {
      // eslint-disable-next-line no-console -- Show warning in production
      console.warn(msg);
    } else {
      throw new Error(msg);
    }

    return;
  }

  if (!properties) {
    (window.va as ((event: string, properties?: unknown) => void) | undefined)?.("event", {
      name,
      options,
    });
    return;
  }

  try {
    const props = parseProperties(properties, {
      strip: isProduction(),
    });

    (window.va as ((event: string, properties?: unknown) => void) | undefined)?.("event", {
      name,
      data: props,
      options,
    });
  } catch (err) {
    if (err instanceof Error && isDevelopment()) {
      // eslint-disable-next-line no-console -- Logging to console is intentional
      console.error(err);
    }
  }
}

/**
 * Identifies a user with their email address. This links the current visitor ID to the provided email.
 * @param email - The email address of the user to identify.
 */
function identify(email: string): void {
  if (!isBrowser()) {
    const msg =
      "[Vuto Web Analytics] Please import `identify` from `@vutolabs/analytics/server` when using this function in a server environment";

    if (isProduction()) {
      // eslint-disable-next-line no-console -- Show warning in production
      console.warn(msg);
    } else {
      throw new Error(msg);
    }

    return;
  }

  if (!email || typeof email !== "string") {
    if (isDevelopment()) {
      // eslint-disable-next-line no-console -- Logging to console is intentional
      console.error(
        "[Vuto Web Analytics] Invalid email provided to identify(). Email must be a non-empty string."
      );
    }
    return;
  }

  // Get the visitor ID from localStorage
  const vid = localStorage.getItem("analytics_vid");

  // Using type assertion to ensure compatibility with the va function
  (window.va as ((event: string, properties?: unknown) => void) | undefined)?.("identify", {
    email,
    vid,
  });
}

export { inject, track, identify, computeRoute };
export type { AnalyticsProps };

// eslint-disable-next-line import/no-default-export -- Default export is intentional
export default {
  inject,
  track,
  identify,
  computeRoute,
};
